import React, { useEffect, useState } from 'react';
import { Alert, Anchor, Badge, Button, Group, Loader, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { apiErrorMessage } from '../../data/apiClient';
import { InvitationPreview, acceptInvitation, showInvitation } from '../../data/Invitation';
import { useCsrf } from '../../hooks/useCsrf';

/**
 * Turn an emailed invitation into an account.
 *
 * The invite is fetched first so the page can show who and where it is for
 * before asking for a password — and so a used, revoked or expired link says so
 * up front rather than after the form is filled in.
 */
const AcceptInviteForm = () => {
    const { csrfToken } = useCsrf();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';

    const [invite, setInvite] = useState<InvitationPreview | null>(null);
    const [loadError, setLoadError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const form = useForm({
        initialValues: { name: '', password: '', confirm: '' },
        validate: {
            name: value => (value.trim().length > 0 ? null : 'Enter your name'),
            // Mirrors Laravel's Password::defaults() (min 8) on the API side.
            password: value => (value.length >= 8 ? null : 'Use at least 8 characters'),
            confirm: (value, values) => (value === values.password ? null : 'Passwords do not match'),
        },
    });

    useEffect(() => {
        if (!token) {
            setLoadError('This invitation link is incomplete.');
            setLoading(false);
            return;
        }

        let cancelled = false;

        showInvitation(token)
            .then(data => {
                if (!cancelled) setInvite(data);
            })
            .catch(err => {
                // 410 carries the reason (used / revoked / expired); 404 means
                // the token simply is not ours.
                if (!cancelled) setLoadError(apiErrorMessage(err, 'This invitation link is not valid.'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleSubmit = async (values: typeof form.values) => {
        setSaving(true);
        setError('');
        try {
            await acceptInvitation(csrfToken, token, {
                name: values.name.trim(),
                password: values.password,
                password_confirmation: values.confirm,
            });

            // Accepting signs them in server-side, so land them in the app with a
            // full reload — useAuth needs to fetch /me against the new session.
            window.location.assign('/');
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not accept this invitation.'));
            setSaving(false);
        }
    };

    const heading = (text: string) => (
        <Text
            style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                lineHeight: 1.1,
                fontWeight: 700,
                color: 'var(--foreground)',
            }}
        >
            {text}
        </Text>
    );

    if (loading) {
        return (
            <AuthLayout>
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            </AuthLayout>
        );
    }

    if (loadError || !invite) {
        return (
            <AuthLayout>
                <Stack gap="lg">
                    {heading('Invitation')}
                    <Alert variant="light" color="red" icon={<IconExclamationCircle size={18} />}>
                        {loadError || 'This invitation link is not valid.'}
                    </Alert>
                    <Anchor onClick={() => navigate('/sign-in')} style={{ fontWeight: 600 }}>
                        Back to sign in
                    </Anchor>
                </Stack>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Stack gap="lg">
                {heading('Accept your invitation')}

                <Stack gap={4}>
                    <Text style={{ color: 'var(--foreground-secondary)' }}>
                        You’ve been invited to join <strong>{invite.organization}</strong>.
                    </Text>
                    <Group gap="xs">
                        <Text size="sm" style={{ color: 'var(--foreground-muted)' }}>
                            {invite.email}
                        </Text>
                        <Badge size="sm" variant="outline" color="gray">
                            {invite.role}
                        </Badge>
                    </Group>
                </Stack>

                {error && (
                    <Alert variant="light" color="red" icon={<IconExclamationCircle size={18} />}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap="sm">
                        <TextInput
                            label="Your name"
                            placeholder="Jane Smith"
                            autoComplete="name"
                            disabled={saving}
                            {...form.getInputProps('name')}
                        />
                        <PasswordInput
                            label="Choose a password"
                            autoComplete="new-password"
                            disabled={saving}
                            {...form.getInputProps('password')}
                        />
                        <PasswordInput
                            label="Confirm password"
                            autoComplete="new-password"
                            disabled={saving}
                            {...form.getInputProps('confirm')}
                        />
                        <Button type="submit" size="md" radius={9999} loading={saving}>
                            Create account
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </AuthLayout>
    );
};

export default AcceptInviteForm;
